import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "dental-google-maps-colorado",
  metaTitle: "Dental Google Maps Optimization in Colorado | GBP for Dentists",
  metaDescription:
    "Rank in the Google Map Pack for dental searches across Colorado — GBP optimization, review velocity, and procedure tuning. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Dental marketing", to: "/dental-marketing-colorado" },
  ],
  currentLabel: "Dental Google Maps Colorado",
  eyebrow: "Dental Google Maps — Colorado",
  h1: (
    <>
      Dental <span className="text-brand-gold">Google Maps</span> in Colorado
    </>
  ),
  heroDescription:
    "Own the Map Pack for dental searches in your Colorado neighborhood — because that's where new-patient phone calls start.",
  intro:
    "Dental Map Pack ranking is the highest-leverage local marketing channel for Colorado practices. We optimize your Google Business Profile with the right primary and secondary categories, every relevant procedure listed as a service, weekly Posts, professional in-office photos, Q&A monitoring, and a HIPAA-compliant review-velocity system tied to your PMS. Most Colorado dental GBPs are 40% optimized — closing that gap is pure ranking gain.",
  definition: {
    term: "Dental Google Maps optimization in Colorado",
    body: "Dental Google Maps optimization is the process of ranking your practice in the Map Pack for high-intent searches like 'dentist near me', 'dental implants Denver', or 'emergency dentist Colorado Springs' through GBP optimization, review velocity, and procedure tuning.",
  },
  whatWeDo: [
    { title: "Full GBP rebuild for dental", body: "Primary category (Dentist), secondary categories (Cosmetic, Pediatric, Emergency, Orthodontist, Implant), every procedure as a service." },
    { title: "Weekly GBP Posts", body: "New-patient specials, procedure highlights, team spotlights, community involvement — published weekly." },
    { title: "Professional photos", body: "Office interior, treatment rooms, dentist and team headshots, completed-case before/afters (with consent) — geo-tagged and uploaded systematically." },
    { title: "Review velocity tied to PMS", body: "HIPAA-compliant review requests sent at the right moment after appointments — tied to Dentrix, Eaglesoft, Open Dental, or your PMS." },
    { title: "Q&A monitoring", body: "Seed common patient questions about insurance, sedation, payment, procedures — monitor and respond promptly." },
    { title: "Service-area tuning", body: "Adjust service-area boundaries and primary location based on actual ranking and patient-acquisition data." },
  ],
  whyThisMarket: [
    {
      heading: "Most dental GBPs are 40% built",
      body: "We routinely audit Colorado dental GBPs with 6–10 services listed when the practice offers 25+. Each missing procedure is a missed ranking opportunity for that exact term.",
    },
    {
      heading: "Reviews drive new-patient acquisition more than any other channel",
      body: "Patients pick dentists by review count and rating. GBP reviews show in the Map Pack, in organic results, and in AI-generated answers. A practice with 200 fresh reviews dominates its zip code; a practice with 40 stale reviews disappears.",
    },
    {
      heading: "Photos and Posts are the most-ignored ranking signals",
      body: "Practices that publish weekly Posts and upload monthly photos rank visibly higher than practices that set up GBP and walk away. It takes 15 minutes a week — most practices skip it.",
    },
  ],
  relatedLinks: [
    { to: "/dental-marketing-colorado", label: "Dental marketing in Colorado — full hub" },
    { to: "/colorado-springs-google-maps", label: "Local SEO overview" },
    { to: "/dental-seo-colorado", label: "Dental SEO in Colorado" },
    { to: "/colorado-springs-google-maps", label: "Colorado Springs Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado dental case studies" },
    { to: "/google-maps", label: "Google Maps optimization (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How fast can a Colorado dental practice rank in the Map Pack?", a: "Most Colorado practices see Map Pack movement in 30–90 days. Suburban and smaller-city markets move faster than Denver and Colorado Springs. Review velocity is the biggest accelerator." },
    { q: "Are dental review-request flows HIPAA-compliant?", a: "Yes — when configured correctly. We use HIPAA-compliant review platforms (Birdeye, Podium, NiceJob) and ensure no PHI flows into the review request itself. Compliant, systematic outreach to satisfied patients only." },
    { q: "Should I list every procedure as a GBP service?", a: "Yes — list every legitimate procedure you offer. Google ranks each independently. An under-listed GBP forfeits ranking on the exact terms patients search." },
    { q: "Do you only serve Colorado Springs dental practices?", a: "No — we serve dental practices across all of Colorado: Denver, Fort Collins, Boulder, Pueblo, Castle Rock, Parker, and the Front Range corridor." },
  ],
  serviceName: "Dental Google Maps Optimization in Colorado",
  serviceDescription:
    "Google Business Profile optimization and Map Pack ranking for dental practices across Colorado.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/dental-google-maps-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
